import React, { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Tool, Employee, Notification, TrainingRecord, AppSettings, GovernanceTask, Curriculum, CurriculumCompletion, DEFAULT_CRITICALITY_LEVELS } from '../types';
import { StatCard } from '../components/StatCard';
import { DollarSign, AlertCircle, CalendarClock, Activity, BookOpen, GraduationCap, ArrowRight, User, X, ShieldCheck, CheckCircle, Search, Database } from 'lucide-react';
import { EmptyState } from '../components/EmptyState';
import { InfoTooltip } from '../components/InfoTooltip';
import { 
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip as RechartsTooltip, ResponsiveContainer, 
  ScatterChart, Scatter, LineChart, Line, Legend, Cell, ReferenceLine, ZAxis, ReferenceArea
} from 'recharts';

interface DashboardViewProps {
  tools: Tool[];
  employees: Employee[];
  notifications: Notification[];
  trainingRecords: TrainingRecord[];
  governanceTasks?: GovernanceTask[];
  curricula?: Curriculum[];
  curriculumCompletions?: CurriculumCompletion[];
  settings: AppSettings;
  onNavigateToNotifications?: () => void;
}

type DashboardTab = 'actions' | 'financials' | 'knowledge';
type TrainingPeriod = 'all' | 'ytd' | 'mtd';

export const DashboardView: React.FC<DashboardViewProps> = ({ tools, employees, notifications, trainingRecords, governanceTasks = [], curricula = [], curriculumCompletions = [], settings, onNavigateToNotifications }) => {
  const navigate = useNavigate();
  const MIN_BUBBLE = 40;
  const MAX_BUBBLE = 400;
  const ZONE_OPACITY = 0.06;
  const MISMATCH_ORANGE = '#fb923c';
  const [activeTab, setActiveTab] = useState<DashboardTab>('actions');
  const [showPendingRequestsModal, setShowPendingRequestsModal] = useState(false);
  const [showGovernanceTasksModal, setShowGovernanceTasksModal] = useState(false);
  const [showPendingTrainingModal, setShowPendingTrainingModal] = useState(false);
  const [showAccessToProvideModal, setShowAccessToProvideModal] = useState(false);
  const [showBudgetOwnersModal, setShowBudgetOwnersModal] = useState(false);
  const [showCostPerEmployeeModal, setShowCostPerEmployeeModal] = useState(false);
  const [showTotalSpendModal, setShowTotalSpendModal] = useState(false);
  const [showKnowledgeScoreModal, setShowKnowledgeScoreModal] = useState(false);
  const [knowledgeScoreType, setKnowledgeScoreType] = useState<'proficiency' | 'satisfaction'>('proficiency');
  const [trainingPeriod, setTrainingPeriod] = useState<TrainingPeriod>('all');
  const [tagCompare1, setTagCompare1] = useState('');
  const [tagCompare2, setTagCompare2] = useState('');
  const [tagCompare3, setTagCompare3] = useState('');
  const [costPeriod, setCostPeriod] = useState<'monthly' | 'annual'>('monthly');
  const costMultiplier = costPeriod === 'annual' ? 12 : 1;

  type CostAxisScale = { divisor: number; suffix: '' | 'K' | 'M' | 'B' };
  const getCostAxisScale = (maxValue: number): CostAxisScale => {
    if (maxValue >= 1_000_000_000) return { divisor: 1_000_000_000, suffix: 'B' };
    if (maxValue >= 1_000_000) return { divisor: 1_000_000, suffix: 'M' };
    if (maxValue >= 10_000) return { divisor: 1_000, suffix: 'K' };
    return { divisor: 1, suffix: '' };
  };
  const formatCostAxisTick = (value: unknown, scale: CostAxisScale): string => {
    const num = Number(value);
    if (!Number.isFinite(num)) return String(value ?? '');
    const scaled = num / scale.divisor;
    return scaled.toLocaleString(undefined, {
      maximumFractionDigits: scale.divisor === 1 ? 0 : 1,
    });
  };
  const formatCostTooltip = (value: unknown): string => {
    const num = Number(value);
    return Number.isFinite(num)
      ? `${settings.currencySymbol}${num.toLocaleString(undefined, { maximumFractionDigits: 0 })}`
      : `${settings.currencySymbol}${String(value ?? '')}`;
  };
  const getCostAxisLabel = (scale: CostAxisScale): string =>
    `${costPeriod === 'annual' ? 'Annual' : 'Monthly'} Cost (${settings.currencySymbol}${scale.suffix ? `, ${scale.suffix}` : ''})`;

  // --- TAB 1: ACTIONS DATA ---
  
  // 1. Governance tasks: next 30 days + overdue (past), non completed
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const in30Days = new Date(today);
  in30Days.setDate(in30Days.getDate() + 30);

  const governanceTasksDue = (governanceTasks || [])
    .filter((t): t is GovernanceTask & { dueDate: string } => {
      if (t.isCompleted || !t.dueDate) return false;
      const d = new Date(t.dueDate);
      d.setHours(0, 0, 0, 0);
      return d <= in30Days;
    })
    .sort((a, b) => new Date(a.dueDate).getTime() - new Date(b.dueDate).getTime());

  // 2. Pending Notifications
  const pendingRequests = notifications.filter(n => n.status === 'PENDING');

  // 4. Pending accesses: admin tasks (ACCESS_REQUEST, TRAINING_REQUEST pending) + responsible tasks (OWNER_TASK pending)
  const accessTasksForAdmin = notifications.filter(
    n => (n.type === 'ACCESS_REQUEST' || n.type === 'TRAINING_REQUEST') && n.status === 'PENDING'
  );
  const accessTasksForResponsibles = notifications.filter(
    n => n.type === 'OWNER_TASK' && n.status === 'PENDING'
  );
  const accessToProvideCount = accessTasksForAdmin.length + accessTasksForResponsibles.length;

  // 3. Pending Training: all (employee, curriculum) or (employee, tool legacy) still pending
  const userCurricula = curricula.filter((c) => (c.audience ?? 'user') !== 'admin');
  const pendingByTool = new Map<string, number>();

  userCurricula.forEach((curriculum) => {
    const tool = tools.find((t) => t.id === curriculum.toolId);
    if (!tool?.assignedUserIds?.length) return;
    tool.assignedUserIds.forEach((uid) => {
      const completed = curriculumCompletions.some(
        (cc) => cc.employeeId === uid && cc.curriculumId === curriculum.id
      );
      if (!completed) {
        const key = tool.id;
        pendingByTool.set(key, (pendingByTool.get(key) ?? 0) + 1);
      }
    });
  });

  // Legacy: tools with trainingUrl but no user curricula
  tools.forEach((tool) => {
    const hasUserCurricula = userCurricula.some((c) => c.toolId === tool.id);
    if (hasUserCurricula || !tool.trainingUrl) return;
    let toolPending = 0;
    tool.assignedUserIds?.forEach((uid) => {
      const hasRecord = trainingRecords.some((r) => r.toolId === tool.id && r.employeeId === uid);
      if (!hasRecord) toolPending++;
    });
    if (toolPending > 0) {
      pendingByTool.set(tool.id, (pendingByTool.get(tool.id) ?? 0) + toolPending);
    }
  });

  const pendingTrainingList: { toolName: string; count: number }[] = Array.from(pendingByTool.entries()).map(
    ([toolId, count]) => ({
      toolName: tools.find((t) => t.id === toolId)?.name ?? toolId,
      count,
    })
  );
  const pendingTrainingCount = pendingTrainingList.reduce((acc, item) => acc + item.count, 0);

  // --- TAB 2: FINANCIALS DATA (conversion vers devise principale) ---
  const getConvertedMonthlyCost = (tool: Tool): number => {
    const main = settings.currency || 'EUR';
    const toolCurrency = tool.currency || main;
    if (toolCurrency === main) return tool.monthlyCost;
    const rate = settings.currencyRates?.find(r => r.code === toolCurrency)?.rateToMain;
    return tool.monthlyCost * (typeof rate === 'number' && rate > 0 ? rate : 1);
  };

  const totalMonthlyCost = tools.reduce((acc, tool) => acc + getConvertedMonthlyCost(tool), 0);

  // Cost per Responsible
  const costByOwner = employees.map(emp => {
      const ownedTools = tools.filter(t => t.powerUserId === emp.id);
      const totalCost = ownedTools.reduce((acc, t) => acc + getConvertedMonthlyCost(t), 0);
      return {
          name: emp.name,
          cost: totalCost,
          toolCount: ownedTools.length
      };
  }).filter(item => item.cost > 0).sort((a,b) => b.cost - a.cost);

  // Cost per team (outil avec 2 équipes = coût divisé par 2 pour chaque équipe)
  const costByTeam = useMemo(() => {
    const map = new Map<string, number>();
    tools.forEach(tool => {
      const teams = tool.teams?.length ? tool.teams : ['No team'];
      const costMain = getConvertedMonthlyCost(tool);
      const share = costMain / teams.length;
      teams.forEach(team => {
        map.set(team, (map.get(team) ?? 0) + share);
      });
    });
    return Array.from(map.entries())
      .map(([name, cost]) => ({ name, cost }))
      .filter(d => d.cost > 0)
      .sort((a, b) => b.cost - a.cost);
  }, [tools, settings.currency, settings.currencyRates]);

  // Moyenne des dépenses par employé
  const avgCostPerEmployee = employees.length > 0 ? totalMonthlyCost / employees.length : 0;

  // Coût par tag sélectionné (pour le graphique comparatif)
  const costByTagCompareData = useMemo(() => {
    const selected = [tagCompare1, tagCompare2, tagCompare3].filter(Boolean);
    return selected.map(tag => ({
      name: tag,
      cost: tools
        .filter(t => t.tags?.includes(tag))
        .reduce((sum, t) => sum + getConvertedMonthlyCost(t), 0),
    }));
  }, [tools, tagCompare1, tagCompare2, tagCompare3, settings.currency, settings.currencyRates]);

  // Matrices Data (coûts convertis en devise principale)
  const scatterData = useMemo(() => tools.map(t => ({
      name: t.name,
      cost: getConvertedMonthlyCost(t),
      satisfaction: t.satisfactionScore / 10, // Scale 1-10
      proficiency: t.teamLiteracy // Scale 1-10
  })), [tools, settings.currency, settings.currencyRates]);

  const costByTeamGraph = useMemo(
    () => costByTeam.map((item) => ({ ...item, cost: item.cost * costMultiplier })),
    [costByTeam, costMultiplier]
  );
  const costByOwnerGraph = useMemo(
    () => costByOwner.map((item) => ({ ...item, cost: item.cost * costMultiplier })),
    [costByOwner, costMultiplier]
  );
  const costByTagCompareGraph = useMemo(
    () => costByTagCompareData.map((item) => ({ ...item, cost: item.cost * costMultiplier })),
    [costByTagCompareData, costMultiplier]
  );
  const scatterCostGraphData = useMemo(
    () => scatterData.map((item) => ({ ...item, cost: item.cost * costMultiplier })),
    [scatterData, costMultiplier]
  );
  const maxTeamCost = Math.max(0, ...costByTeamGraph.map((d) => d.cost));
  const maxOwnerCost = Math.max(0, ...costByOwnerGraph.map((d) => d.cost));
  const maxTagCost = Math.max(0, ...costByTagCompareGraph.map((d) => d.cost));
  const maxScatterCost = Math.max(0, ...scatterCostGraphData.map((d) => d.cost));
  const teamAxisScale = getCostAxisScale(maxTeamCost);
  const ownerAxisScale = getCostAxisScale(maxOwnerCost);
  const tagAxisScale = getCostAxisScale(maxTagCost);
  const scatterAxisScale = getCostAxisScale(maxScatterCost);

  const costRankMap = useMemo(() => {
    const map = new Map<string, number>();
    const sortedByCost = [...tools].sort((a, b) => (b.monthlyCost ?? 0) - (a.monthlyCost ?? 0));
    let currentRank = 0;
    for (let i = 0; i < sortedByCost.length; i++) {
      if (i > 0 && (sortedByCost[i].monthlyCost ?? 0) < (sortedByCost[i - 1].monthlyCost ?? 0)) {
        currentRank = i;
      }
      map.set(sortedByCost[i].id, currentRank);
    }
    return map;
  }, [tools]);

  const getBubbleSize = (toolId: string) => {
    if (tools.length <= 1) return (MIN_BUBBLE + MAX_BUBBLE) / 2;
    const rank = costRankMap.get(toolId) ?? tools.length - 1;
    return MAX_BUBBLE - (rank / (tools.length - 1)) * (MAX_BUBBLE - MIN_BUBBLE);
  };

  const knowledgeMatrixData = useMemo(() => tools.map(t => {
    const sat = t.satisfactionScore / 10;
    const prof = t.teamLiteracy;
    const color =
      (sat < 5 || prof < 5) ? '#ef4444' :
      (prof >= 7 && sat >= 7) ? '#22c55e' :
      MISMATCH_ORANGE;
    return {
      id: t.id,
      name: t.name,
      satisfaction: sat,
      proficiency: prof,
      cost: t.monthlyCost ?? 0,
      z: getBubbleSize(t.id),
      color,
    };
  }), [tools, costRankMap]);

  // --- TAB 3: KNOWLEDGE DATA ---
  
  // Training stats by selected period (All time / YTD / MTD)
  const totalTrainingDone = useMemo(() => {
    if (trainingPeriod === 'all') return trainingRecords.length;
    const now = new Date();
    const start = trainingPeriod === 'ytd'
      ? new Date(now.getFullYear(), 0, 1)
      : new Date(now.getFullYear(), now.getMonth(), 1);
    const startTs = start.getTime();
    return trainingRecords.filter((record) => {
      const ts = new Date(record.completedAt).getTime();
      return Number.isFinite(ts) && ts >= startTs;
    }).length;
  }, [trainingRecords, trainingPeriod]);
  const trainingPeriodLabel = trainingPeriod === 'all' ? 'All time' : trainingPeriod.toUpperCase();
  // Evolution Data (Mocked based on feedback history as training records don't track history well in current simple schema, 
  // but we can assume feedback dates correlate with knowledge checks)
  
  // Build time series from all feedback to show evolution of Sat/Prof
  const allFeedback = tools.flatMap(t => t.feedback || []);
  const evolutionDataMap = new Map<string, { date: string, satSum: number, profSum: number, count: number }>();
  
  allFeedback.forEach(f => {
      const dateKey = f.date.substring(0, 7); // YYYY-MM
      if (!evolutionDataMap.has(dateKey)) {
          evolutionDataMap.set(dateKey, { date: dateKey, satSum: 0, profSum: 0, count: 0 });
      }
      const entry = evolutionDataMap.get(dateKey)!;
      entry.satSum += f.satisfaction;
      entry.profSum += f.proficiency;
      entry.count += 1;
  });

  const evolutionData = Array.from(evolutionDataMap.values())
      .map(e => ({
          date: e.date,
          avgSatisfaction: parseFloat((e.satSum / e.count).toFixed(1)),
          avgProficiency: parseFloat((e.profSum / e.count).toFixed(1))
      }))
      .sort((a,b) => a.date.localeCompare(b.date));
      
  // If no history, add current state as a single point
  if (evolutionData.length === 0 && tools.length > 0) {
      const totalSat = tools.reduce((acc, t) => acc + t.satisfactionScore, 0) / tools.length / 10;
      const totalProf = tools.reduce((acc, t) => acc + t.teamLiteracy, 0) / tools.length;
      evolutionData.push({
          date: new Date().toISOString().substring(0, 7),
          avgSatisfaction: parseFloat(totalSat.toFixed(1)),
          avgProficiency: parseFloat(totalProf.toFixed(1))
      });
  }

  const overallAvgProficiency = tools.reduce((acc, t) => acc + t.teamLiteracy, 0) / (tools.length || 1);
  const overallAvgSat = tools.reduce((acc, t) => acc + (t.satisfactionScore/10), 0) / (tools.length || 1);
  const knowledgeScoreRows = useMemo(() => {
    return tools.map((tool) => {
      const feedback = tool.feedback || [];
      const hasScore = knowledgeScoreType === 'proficiency'
        ? feedback.some((f) => typeof f.proficiency === 'number')
        : feedback.some((f) => typeof f.satisfaction === 'number');
      const score = knowledgeScoreType === 'proficiency'
        ? tool.teamLiteracy
        : tool.satisfactionScore / 10;
      return { toolName: tool.name, hasScore, score };
    }).sort((a, b) => {
      if (a.hasScore !== b.hasScore) return a.hasScore ? -1 : 1;
      if (!a.hasScore && !b.hasScore) return a.toolName.localeCompare(b.toolName);
      return b.score - a.score;
    });
  }, [tools, knowledgeScoreType]);


  return (
    <div className="space-y-6 animate-fade-in">
      
      {/* Dashboard Tabs */}
      <div className="w-full mb-6">
        <div className="grid w-full grid-cols-3 gap-1 bg-slate-200 p-1 rounded-xl">
        <button
          onClick={() => setActiveTab('actions')}
          className={`px-2 sm:px-4 py-2 rounded-lg text-xs sm:text-sm font-medium transition-all flex items-center justify-center gap-1.5 sm:gap-2 whitespace-nowrap ${
            activeTab === 'actions' ? 'bg-toggle text-white shadow-sm' : 'text-slate-500 hover:text-slate-700'
          }`}
        >
          <AlertCircle size={16} />
          <span className="sm:hidden">Actions</span>
          <span className="hidden sm:inline">Action Center</span>
        </button>
        <button
          onClick={() => setActiveTab('financials')}
          className={`px-2 sm:px-4 py-2 rounded-lg text-xs sm:text-sm font-medium transition-all flex items-center justify-center gap-1.5 sm:gap-2 whitespace-nowrap ${
            activeTab === 'financials' ? 'bg-toggle text-white shadow-sm' : 'text-slate-500 hover:text-slate-700'
          }`}
        >
          <DollarSign size={16} />
          <span className="sm:hidden">Finance</span>
          <span className="hidden sm:inline">Financial Overview</span>
        </button>
        <button
          onClick={() => setActiveTab('knowledge')}
          className={`px-2 sm:px-4 py-2 rounded-lg text-xs sm:text-sm font-medium transition-all flex items-center justify-center gap-1.5 sm:gap-2 whitespace-nowrap ${
            activeTab === 'knowledge' ? 'bg-toggle text-white shadow-sm' : 'text-slate-500 hover:text-slate-700'
          }`}
        >
          <Activity size={16} />
          <span className="sm:hidden">Knowledge</span>
          <span className="hidden sm:inline">Knowledge & Growth</span>
        </button>
      </div>
      </div>

      {/* --- TAB 1: ACTIONS --- */}
      {activeTab === 'actions' && (
        <div className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                <StatCard 
                    label="Governance tasks" 
                    value={governanceTasksDue.length} 
                    subValue="Next 30 days or overdue"
                    icon={<CalendarClock size={20} />}
                    color="indigo"
                    onClick={() => setShowGovernanceTasksModal(true)}
                />
                <StatCard 
                    label="Pending Requests" 
                    value={pendingRequests.length} 
                    subValue="Access & Tools"
                    icon={<User size={20} />}
                    color="blue"
                    onClick={() => setShowPendingRequestsModal(true)}
                />
                <StatCard 
                    label="Pending Training" 
                    value={pendingTrainingCount} 
                    subValue="Modules Incomplete"
                    icon={<BookOpen size={20} />}
                    color="amber"
                    onClick={() => setShowPendingTrainingModal(true)}
                />
                <StatCard 
                    label="Pending accesses" 
                    value={accessToProvideCount} 
                    subValue={[accessTasksForAdmin.length ? `${accessTasksForAdmin.length} admin` : null, accessTasksForResponsibles.length ? `${accessTasksForResponsibles.length} responsibles` : null].filter(Boolean).join(' • ') || 'Tasks'}
                    icon={<ShieldCheck size={20} />}
                    color="emerald"
                    onClick={() => setShowAccessToProvideModal(true)}
                />
            </div>

            <CriticalityBreakdown tools={tools} governanceTasks={governanceTasks} settings={settings} />

        </div>
      )}

      {/* --- TAB 2: FINANCIALS --- */}
      {activeTab === 'financials' && (
        <div className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                <StatCard 
                    label="Total Monthly Spend" 
                    value={`${settings.currencySymbol}${totalMonthlyCost.toLocaleString()}`} 
                    subValue={`${settings.currencySymbol}${(totalMonthlyCost * 12).toLocaleString()} / year`}
                    icon={<DollarSign size={20} />}
                    color="blue"
                    onClick={() => setShowTotalSpendModal(true)}
                />
                 {/* Top Cost Owner */}
                 <StatCard
                    label="Top Budget Owner"
                    value={costByOwner[0]?.name || '-'}
                    subValue={`Controls ${settings.currencySymbol}${costByOwner[0]?.cost.toLocaleString()}/mo`}
                    icon={<User size={20} />}
                    color="emerald"
                    onClick={() => setShowBudgetOwnersModal(true)}
                />
                <StatCard
                    label="Average per employee"
                    value={`${settings.currencySymbol}${avgCostPerEmployee.toLocaleString(undefined, { maximumFractionDigits: 0 })}`}
                    subValue={`${employees.length} employee${employees.length !== 1 ? 's' : ''}`}
                    icon={<User size={20} />}
                    color="slate"
                    onClick={() => setShowCostPerEmployeeModal(true)}
                />
            </div>

            <div className="flex justify-end">
              <div className="inline-flex items-center bg-slate-100 rounded-lg p-1">
                <button
                  type="button"
                  onClick={() => setCostPeriod('monthly')}
                  className={`px-3 py-1 text-sm rounded-md transition-colors ${costPeriod === 'monthly' ? 'bg-white shadow text-slate-800 font-medium' : 'text-slate-500 hover:text-slate-700'}`}
                >
                  Monthly
                </button>
                <button
                  type="button"
                  onClick={() => setCostPeriod('annual')}
                  className={`px-3 py-1 text-sm rounded-md transition-colors ${costPeriod === 'annual' ? 'bg-white shadow text-slate-800 font-medium' : 'text-slate-500 hover:text-slate-700'}`}
                >
                  Annual
                </button>
              </div>
            </div>

            {/* Cost by team (répartition par équipe, coût divisé si outil multi-équipes) */}
            <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-6">
                <h3 className="text-lg font-bold text-slate-800 mb-4">Cost by team <span className="hidden sm:inline-flex"><InfoTooltip text={`${costPeriod === 'annual' ? 'Annual' : 'Monthly'} spend broken down by team assignment.`} /></span></h3>
                <p className="text-xs text-slate-500 mb-4">When a tool is assigned to several teams, its cost is split equally.</p>
                <div className="h-64 relative">
                    <div className="absolute left-8 top-1 z-10 text-[10px] text-slate-500 bg-white/80 px-1 rounded pointer-events-none">
                      {getCostAxisLabel(teamAxisScale)}
                    </div>
                    {costByTeamGraph.length === 0 ? (
                        <EmptyState
                          icon={<Database size={40} />}
                          title="No team data yet"
                          subtitle="Assign teams to your tools in the Inventory to see cost breakdown here."
                          action={{ label: 'Go to Inventory', onClick: () => navigate('/admin/inventory') }}
                        />
                    ) : (
                        <ResponsiveContainer width="100%" height="100%">
                            <BarChart data={costByTeamGraph} margin={{ top: 52, right: 30, left: 0, bottom: 0 }}>
                                <CartesianGrid strokeDasharray="3 3" vertical={false} />
                                <XAxis dataKey="name" tick={{ fontSize: 12 }} />
                                <YAxis tickFormatter={(val) => formatCostAxisTick(val, teamAxisScale)} />
                                <RechartsTooltip formatter={(val) => formatCostTooltip(val)} />
                                <Bar dataKey="cost" fill="#10b981" radius={[4, 4, 0, 0]} barSize={40} />
                            </BarChart>
                        </ResponsiveContainer>
                    )}
                </div>
            </div>

            {/* Cost Ownership Chart */}
            <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-6">
                <h3 className="text-lg font-bold text-slate-800 mb-4">Cost by Responsible</h3>
                <div className="h-64 relative">
                    <div className="absolute left-8 top-1 z-10 text-[10px] text-slate-500 bg-white/80 px-1 rounded pointer-events-none">
                      {getCostAxisLabel(ownerAxisScale)}
                    </div>
                    <ResponsiveContainer width="100%" height="100%">
                        <BarChart data={costByOwnerGraph} margin={{ top: 52, right: 30, left: 0, bottom: 0 }}>
                            <CartesianGrid strokeDasharray="3 3" vertical={false} />
                            <XAxis dataKey="name" tick={{fontSize: 12}} />
                            <YAxis tickFormatter={(val) => formatCostAxisTick(val, ownerAxisScale)} />
                            <RechartsTooltip formatter={(val) => formatCostTooltip(val)} />
                            <Bar dataKey="cost" fill="#3b82f6" radius={[4, 4, 0, 0]} barSize={40} />
                        </BarChart>
                    </ResponsiveContainer>
                </div>
            </div>

            {/* Compare cost by tag (3 dropdowns → 3 barres) */}
            <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-6">
                <h3 className="text-lg font-bold text-slate-800 mb-4">Cost by tag (compare up to 3 tags) <span className="hidden sm:inline-flex"><InfoTooltip text="Compare costs for up to 3 tags at once." /></span></h3>
                <p className="text-xs text-slate-500 mb-4">Select tags from configuration to compare total cost of tools that have each tag.</p>
                <div className="flex flex-wrap gap-4 mb-6">
                    <div>
                        <label className="block text-xs font-bold text-slate-500 uppercase mb-1">Tag 1</label>
                        <select
                            value={tagCompare1}
                            onChange={e => setTagCompare1(e.target.value)}
                            className="w-40 p-2 border border-slate-300 rounded-lg bg-white text-sm"
                        >
                            <option value="">— Select —</option>
                            {settings.tags?.map(tag => (
                                <option key={tag} value={tag}>{tag}</option>
                            ))}
                        </select>
                    </div>
                    <div>
                        <label className="block text-xs font-bold text-slate-500 uppercase mb-1">Tag 2</label>
                        <select
                            value={tagCompare2}
                            onChange={e => setTagCompare2(e.target.value)}
                            className="w-40 p-2 border border-slate-300 rounded-lg bg-white text-sm"
                        >
                            <option value="">— Select —</option>
                            {settings.tags?.map(tag => (
                                <option key={tag} value={tag}>{tag}</option>
                            ))}
                        </select>
                    </div>
                    <div>
                        <label className="block text-xs font-bold text-slate-500 uppercase mb-1">Tag 3</label>
                        <select
                            value={tagCompare3}
                            onChange={e => setTagCompare3(e.target.value)}
                            className="w-40 p-2 border border-slate-300 rounded-lg bg-white text-sm"
                        >
                            <option value="">— Select —</option>
                            {settings.tags?.map(tag => (
                                <option key={tag} value={tag}>{tag}</option>
                            ))}
                        </select>
                    </div>
                </div>
                <div className="h-64 relative">
                    <div className="absolute left-8 top-1 z-10 text-[10px] text-slate-500 bg-white/80 px-1 rounded pointer-events-none">
                      {getCostAxisLabel(tagAxisScale)}
                    </div>
                    {costByTagCompareGraph.length === 0 ? (
                        <EmptyState
                          icon={<Search size={40} />}
                          title="Compare tag costs"
                          subtitle="Pick 1-3 tags above to see a side-by-side spending comparison."
                        />
                    ) : (
                        <ResponsiveContainer width="100%" height="100%">
                            <BarChart data={costByTagCompareGraph} margin={{ top: 52, right: 30, left: 0, bottom: 0 }}>
                                <CartesianGrid strokeDasharray="3 3" vertical={false} />
                                <XAxis dataKey="name" tick={{ fontSize: 12 }} />
                                <YAxis tickFormatter={(val) => formatCostAxisTick(val, tagAxisScale)} />
                                <RechartsTooltip formatter={(val) => formatCostTooltip(val)} />
                                <Bar dataKey="cost" fill="#8b5cf6" radius={[4, 4, 0, 0]} barSize={40} />
                            </BarChart>
                        </ResponsiveContainer>
                    )}
                </div>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {/* Cost vs Satisfaction */}
                <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-4 sm:p-6">
                    <h3 className="text-base sm:text-lg font-bold text-slate-800 mb-4 flex items-center gap-2">
                      <span className="sm:hidden">Cost matrix</span>
                      <span className="hidden sm:inline">Cost vs. Satisfaction</span>
                      <span className="hidden sm:inline-flex"><InfoTooltip text="Are we getting value for money?" /></span>
                    </h3>
                    <div className="h-64 relative">
                        <div className="absolute left-8 top-1 z-10 text-[10px] text-slate-500 bg-white/80 px-1 rounded pointer-events-none">
                          {getCostAxisLabel(scatterAxisScale)}
                        </div>
                        <ResponsiveContainer width="100%" height="100%">
                            <ScatterChart margin={{ top: 52, right: 8, bottom: 16, left: 0 }}>
                                <CartesianGrid strokeDasharray="3 3" />
                                <XAxis type="number" dataKey="satisfaction" name="Satisfaction" domain={[0, 10]} />
                                <YAxis
                                  type="number"
                                  dataKey="cost"
                                  name={getCostAxisLabel(scatterAxisScale)}
                                  tickFormatter={(val) => formatCostAxisTick(val, scatterAxisScale)}
                                />
                                <RechartsTooltip
                                  cursor={{ strokeDasharray: '3 3' }}
                                  formatter={(value, name) => [
                                    name === 'cost' ? formatCostTooltip(value) : value,
                                    name === 'cost' ? getCostAxisLabel(scatterAxisScale) : name,
                                  ]}
                                />
                                <Scatter name="Tools" data={scatterCostGraphData} fill="#8b5cf6" />
                            </ScatterChart>
                        </ResponsiveContainer>
                    </div>
                </div>

                {/* Cost vs Proficiency */}
                <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-4 sm:p-6">
                    <h3 className="text-lg font-bold text-slate-800 mb-4">Cost vs. Proficiency <span className="hidden sm:inline-flex"><InfoTooltip text="Are expensive tools too complex?" /></span></h3>
                    <div className="h-64 relative">
                         <div className="absolute left-8 top-1 z-10 text-[10px] text-slate-500 bg-white/80 px-1 rounded pointer-events-none">
                           {getCostAxisLabel(scatterAxisScale)}
                         </div>
                         <ResponsiveContainer width="100%" height="100%">
                            <ScatterChart margin={{ top: 52, right: 8, bottom: 16, left: 0 }}>
                                <CartesianGrid strokeDasharray="3 3" />
                                <XAxis type="number" dataKey="proficiency" name="Proficiency" domain={[0, 10]} />
                                <YAxis
                                  type="number"
                                  dataKey="cost"
                                  name={getCostAxisLabel(scatterAxisScale)}
                                  tickFormatter={(val) => formatCostAxisTick(val, scatterAxisScale)}
                                />
                                <RechartsTooltip
                                  cursor={{ strokeDasharray: '3 3' }}
                                  formatter={(value, name) => [
                                    name === 'cost' ? formatCostTooltip(value) : value,
                                    name === 'cost' ? getCostAxisLabel(scatterAxisScale) : name,
                                  ]}
                                />
                                <Scatter name="Tools" data={scatterCostGraphData} fill="#10b981" />
                            </ScatterChart>
                        </ResponsiveContainer>
                    </div>
                </div>
            </div>
        </div>
      )}

      {/* --- TAB 3: KNOWLEDGE --- */}
      {activeTab === 'knowledge' && (
          <div className="space-y-6">
               <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-5 flex flex-col justify-between">
                  <div className="flex justify-between items-start">
                    <div>
                      <p className="text-sm font-medium text-slate-500">Total Training Completed</p>
                      <h3 className="text-2xl font-bold text-slate-800 mt-1">{totalTrainingDone}</h3>
                      <div className="mt-2 flex items-center gap-2">
                        <div className="inline-flex items-center bg-slate-100 rounded-lg p-1">
                          <button
                            type="button"
                            onClick={() => setTrainingPeriod('all')}
                            className={`px-2 py-1 text-xs rounded-md transition-colors ${trainingPeriod === 'all' ? 'bg-white shadow text-slate-800 font-medium' : 'text-slate-500 hover:text-slate-700'}`}
                          >
                            All
                          </button>
                          <button
                            type="button"
                            onClick={() => setTrainingPeriod('ytd')}
                            className={`px-2 py-1 text-xs rounded-md transition-colors ${trainingPeriod === 'ytd' ? 'bg-white shadow text-slate-800 font-medium' : 'text-slate-500 hover:text-slate-700'}`}
                          >
                            YTD
                          </button>
                          <button
                            type="button"
                            onClick={() => setTrainingPeriod('mtd')}
                            className={`px-2 py-1 text-xs rounded-md transition-colors ${trainingPeriod === 'mtd' ? 'bg-white shadow text-slate-800 font-medium' : 'text-slate-500 hover:text-slate-700'}`}
                          >
                            MTD
                          </button>
                        </div>
                        <InfoTooltip text="All = all completed trainings. YTD = since January 1st. MTD = since the first day of this month." />
                      </div>
                      <p className="text-xs text-slate-400 mt-1">{trainingPeriodLabel}</p>
                    </div>
                    <div className="p-2 rounded-lg bg-emerald-50 text-emerald-600">
                      <GraduationCap size={20} />
                    </div>
                  </div>
                </div>
                <StatCard 
                    label="Avg. Proficiency" 
                    value={overallAvgProficiency.toFixed(1)} 
                    subValue="Out of 10"
                    icon={<BookOpen size={20} />}
                    color="blue"
                    onClick={() => {
                      setKnowledgeScoreType('proficiency');
                      setShowKnowledgeScoreModal(true);
                    }}
                />
                <StatCard 
                    label="Avg. Satisfaction" 
                    value={overallAvgSat.toFixed(1)} 
                    subValue="Out of 10"
                    icon={<Activity size={20} />}
                    color="indigo"
                    onClick={() => {
                      setKnowledgeScoreType('satisfaction');
                      setShowKnowledgeScoreModal(true);
                    }}
                />
            </div>

            {/* Tool Matrix (moved from dedicated page) */}
            <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-6">
                <h3 className="text-lg font-bold text-slate-800 mb-2">Tool Matrix</h3>
                <p className="text-xs text-slate-500 mb-4">Satisfaction vs proficiency across your tool stack.</p>
                <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                  <div className="lg:col-span-2">
                    <div className="w-full max-w-[560px] mr-auto aspect-square">
                      {knowledgeMatrixData.length === 0 ? (
                          <EmptyState
                            icon={<Activity size={40} />}
                            title="No matrix data yet"
                            subtitle="Add tools and collect feedback to populate the matrix."
                          />
                      ) : (
                          <ResponsiveContainer width="100%" height="100%">
                              <ScatterChart margin={{ top: 20, right: 20, bottom: 20, left: 10 }}>
                                  <ReferenceArea x1={0} x2={10} y1={0} y2={5} fill="#ef4444" fillOpacity={ZONE_OPACITY} />
                                  <ReferenceArea x1={0} x2={5} y1={5} y2={10} fill="#ef4444" fillOpacity={ZONE_OPACITY} />
                                  <ReferenceArea x1={5} x2={7} y1={5} y2={7} fill={MISMATCH_ORANGE} fillOpacity={ZONE_OPACITY} />
                                  <ReferenceArea x1={5} x2={7} y1={7} y2={10} fill={MISMATCH_ORANGE} fillOpacity={ZONE_OPACITY} />
                                  <ReferenceArea x1={7} x2={10} y1={5} y2={7} fill={MISMATCH_ORANGE} fillOpacity={ZONE_OPACITY} />
                                  <ReferenceArea x1={7} x2={10} y1={7} y2={10} fill="#10b981" fillOpacity={ZONE_OPACITY} />
                                  <XAxis
                                    type="number"
                                    dataKey="proficiency"
                                    name="Proficiency"
                                    domain={[0, 10]}
                                    ticks={[0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10]}
                                    label={{ value: 'Team Proficiency (Literacy)', position: 'bottom', offset: 0 }}
                                  />
                                  <YAxis
                                    type="number"
                                    dataKey="satisfaction"
                                    name="Satisfaction"
                                    domain={[0, 10]}
                                    ticks={[0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10]}
                                    label={{ value: 'Satisfaction', angle: -90, position: 'insideLeft' }}
                                  />
                                  <ZAxis type="number" dataKey="z" range={[MIN_BUBBLE, MAX_BUBBLE]} name="Cost" />
                                  <ReferenceLine x={5} stroke="#cbd5e1" strokeDasharray="4 4" />
                                  <ReferenceLine y={5} stroke="#cbd5e1" strokeDasharray="4 4" />
                                  <ReferenceLine x={7} stroke="#cbd5e1" strokeDasharray="4 4" />
                                  <ReferenceLine y={7} stroke="#cbd5e1" strokeDasharray="4 4" />
                                  <RechartsTooltip
                                    cursor={false}
                                    formatter={(value: unknown, name: string) => [
                                      typeof value === 'number' ? value.toFixed(1) : value,
                                      name === 'proficiency'
                                        ? 'Proficiency'
                                        : name === 'satisfaction'
                                          ? 'Satisfaction'
                                          : 'Monthly cost',
                                    ]}
                                    labelFormatter={(_, payload) => {
                                      const item = Array.isArray(payload) ? payload[0]?.payload : null;
                                      return item?.name ?? '';
                                    }}
                                  />
                                  <Scatter data={knowledgeMatrixData}>
                                    {knowledgeMatrixData.map((entry, idx) => (
                                      <Cell key={`${entry.name}-${idx}`} fill={entry.color} />
                                    ))}
                                  </Scatter>
                              </ScatterChart>
                          </ResponsiveContainer>
                      )}
                    </div>
                    {knowledgeMatrixData.length > 0 && (
                      <>
                        <div className="w-full max-w-[560px] mx-auto">
                          <div className="flex flex-wrap justify-center gap-6 mt-4 text-xs font-medium">
                            <div className="flex items-center gap-1"><div className="w-3 h-3 rounded-full bg-red-500" /> Critical Issue</div>
                            <div className="flex items-center gap-1"><div className="w-3 h-3 rounded-full bg-orange-400" /> Tool Mismatch</div>
                            <div className="flex items-center gap-1"><div className="w-3 h-3 rounded-full bg-emerald-500" /> Optimized</div>
                          </div>
                          <div className="flex justify-center items-center gap-4 mt-2 text-xs text-slate-400">
                            <div className="flex items-center gap-1.5">
                              <svg width="10" height="10"><circle cx="5" cy="5" r="3" fill="#94a3b8" /></svg>
                              Lowest cost
                            </div>
                            <div className="flex items-center gap-1.5">
                              <svg width="20" height="20"><circle cx="10" cy="10" r="9" fill="#94a3b8" /></svg>
                              Highest cost
                            </div>
                          </div>
                        </div>
                      </>
                    )}
                  </div>

                  <div className="lg:col-span-1">
                    <div className="h-full bg-slate-50 rounded-xl border border-slate-200 p-4">
                      <h4 className="text-sm font-bold text-slate-800 mb-3">How to read this matrix</h4>
                      <div className="space-y-3 text-sm text-slate-600">
                        <p>
                          <span className="font-semibold text-red-600">Critical Issue:</span> Satisfaction or proficiency is below 5. Immediate action is recommended.
                        </p>
                        <p>
                          <span className="font-semibold text-orange-500">Tool Mismatch:</span> Tool is not critical but not optimized yet. Improve adoption, training, or evaluate alternatives.
                        </p>
                        <p>
                          <span className="font-semibold text-emerald-600">Optimized:</span> High satisfaction and high proficiency (7+). Keep current setup and monitor over time.
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
            </div>

            {/* Evolution Graph */}
            <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-6">
                <h3 className="text-lg font-bold text-slate-800 mb-4">Knowledge & Sentiment Evolution</h3>
                <div className="h-80">
                    <ResponsiveContainer width="100%" height="100%">
                        <LineChart data={evolutionData} margin={{ top: 5, right: 30, left: 20, bottom: 5 }}>
                            <CartesianGrid strokeDasharray="3 3" vertical={false} />
                            <XAxis dataKey="date" />
                            <YAxis domain={[0, 10]} />
                            <RechartsTooltip />
                            <Legend />
                            <Line type="monotone" dataKey="avgProficiency" name="Avg Proficiency" stroke="#10b981" strokeWidth={2} />
                            <Line type="monotone" dataKey="avgSatisfaction" name="Avg Satisfaction" stroke="#6366f1" strokeWidth={2} />
                        </LineChart>
                    </ResponsiveContainer>
                </div>
            </div>
          </div>
      )}

      {/* --- MODALS --- */}
      
      {/* Pending Requests Modal */}
      {showPendingRequestsModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 backdrop-blur-sm p-4 animate-in fade-in duration-200">
            <div className="bg-white rounded-xl shadow-xl w-full max-w-2xl max-h-[90vh] flex flex-col overflow-hidden">
                <div className="flex justify-between items-center p-6 border-b border-slate-100 bg-slate-50">
                    <h3 className="text-lg font-bold text-slate-800">Pending Requests</h3>
                    <button onClick={() => setShowPendingRequestsModal(false)} className="text-slate-400 hover:text-slate-600">
                        <X size={24} />
                    </button>
                </div>
                <div className="p-6 overflow-y-auto">
                    {pendingRequests.length === 0 ? (
                        <EmptyState icon={<CheckCircle size={40} />} title="No pending requests" subtitle="All employee requests have been handled." positive />
                    ) : (
                        <div className="space-y-3">
                            {pendingRequests.map(n => (
                                <div key={n.id} className="p-4 border border-slate-200 rounded-lg hover:bg-slate-50 transition-colors">
                                    <div className="flex justify-between items-start mb-1">
                                        <span className="text-xs font-bold text-slate-500 uppercase">{n.type.replace('_', ' ')}</span>
                                        <span className="text-xs text-slate-400">{new Date(n.createdAt).toLocaleDateString()}</span>
                                    </div>
                                    <div className="font-bold text-slate-800">{n.requesterName}</div>
                                    <div className="text-sm text-slate-600">
                                        {n.type === 'NEW_TOOL_REQUEST' ? (
                                            <span>Requested new tool: <span className="font-semibold text-indigo-600">{n.newToolData?.name}</span></span>
                                        ) : (
                                            <span>Request for: <span className="font-semibold text-indigo-600">{n.toolName}</span></span>
                                        )}
                                    </div>
                                    {(n.accessReason || n.trainingNeeds) && (
                                         <div className="mt-2 text-xs text-slate-500 bg-slate-100 p-2 rounded">
                                            "{n.accessReason || n.trainingNeeds}"
                                         </div>
                                    )}
                                </div>
                            ))}
                        </div>
                    )}
                </div>
                <div className="p-4 border-t border-slate-100 bg-slate-50 flex justify-between items-center">
                    <button 
                        onClick={() => setShowPendingRequestsModal(false)}
                        className="px-4 py-2 bg-white border border-slate-200 text-slate-700 font-medium rounded-lg hover:bg-slate-100"
                    >
                        Close
                    </button>
                    {pendingRequests.length > 0 && (
                      <button
                        onClick={() => { setShowPendingRequestsModal(false); navigate('/admin/notifications'); }}
                        className="px-4 py-2 bg-indigo-600 text-white font-medium rounded-lg hover:bg-indigo-700 flex items-center gap-2"
                      >
                        Go to Notifications <ArrowRight size={16} />
                      </button>
                    )}
                </div>
            </div>
        </div>
      )}

      {/* Governance tasks Modal */}
      {showGovernanceTasksModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 backdrop-blur-sm p-4 animate-in fade-in duration-200">
            <div className="bg-white rounded-xl shadow-xl w-full max-w-2xl max-h-[90vh] flex flex-col overflow-hidden">
                <div className="flex justify-between items-center p-6 border-b border-slate-100 bg-slate-50">
                    <h3 className="text-lg font-bold text-slate-800">Governance tasks</h3>
                    <button onClick={() => setShowGovernanceTasksModal(false)} className="text-slate-400 hover:text-slate-600">
                        <X size={24} />
                    </button>
                </div>
                <div className="p-6 overflow-y-auto">
                    {governanceTasksDue.length === 0 ? (
                        <EmptyState icon={<CheckCircle size={40} />} title="All clear" subtitle="No governance tasks due soon. Tasks are auto-created from tool renewal dates." positive />
                    ) : (
                        <div className="space-y-3">
                            {governanceTasksDue.map(task => {
                                const dueDate = task.dueDate ? new Date(task.dueDate) : null;
                                const isOverdue = dueDate && dueDate < today;
                                const toolName = task.toolId ? tools.find(t => t.id === task.toolId)?.name : null;
                                return (
                                <div key={task.id} className={`p-4 border rounded-lg hover:bg-slate-50 transition-colors ${isOverdue ? 'border-amber-200 bg-amber-50/30' : 'border-slate-200'}`}>
                                    <div className="flex justify-between items-start">
                                        <div>
                                            <div className="font-bold text-slate-800">{task.title}</div>
                                            {toolName && <div className="text-xs text-slate-500">Tool: {toolName}</div>}
                                        </div>
                                        <div className={`px-2 py-1 rounded text-xs font-bold ${isOverdue ? 'bg-amber-100 text-amber-700' : 'bg-indigo-50 text-indigo-700'}`}>
                                            {isOverdue ? 'Overdue' : 'Due'}
                                        </div>
                                    </div>
                                    <div className="mt-3 flex gap-4 text-sm text-slate-600">
                                        <span className="flex items-center gap-1.5">
                                            <CalendarClock size={14} className="text-slate-400" />
                                            Due: <span className="font-medium">{task.dueDate}</span>
                                        </span>
                                    </div>
                                </div>
                            );})}
                        </div>
                    )}
                </div>
                <div className="p-4 border-t border-slate-100 bg-slate-50 flex justify-between items-center">
                    <button onClick={() => setShowGovernanceTasksModal(false)} className="px-4 py-2 bg-white border border-slate-200 text-slate-700 font-medium rounded-lg hover:bg-slate-100">
                        Close
                    </button>
                    {governanceTasksDue.length > 0 && (
                      <button
                        onClick={() => { setShowGovernanceTasksModal(false); navigate('/admin/governance'); }}
                        className="px-4 py-2 bg-indigo-600 text-white font-medium rounded-lg hover:bg-indigo-700 flex items-center gap-2"
                      >
                        Go to Governance <ArrowRight size={16} />
                      </button>
                    )}
                </div>
            </div>
        </div>
      )}

      {/* Pending Training Modal */}
      {showPendingTrainingModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 backdrop-blur-sm p-4 animate-in fade-in duration-200">
            <div className="bg-white rounded-xl shadow-xl w-full max-w-2xl max-h-[90vh] flex flex-col overflow-hidden">
                <div className="flex justify-between items-center p-6 border-b border-slate-100 bg-slate-50">
                    <h3 className="text-lg font-bold text-slate-800">Pending Training Modules</h3>
                    <button onClick={() => setShowPendingTrainingModal(false)} className="text-slate-400 hover:text-slate-600">
                        <X size={24} />
                    </button>
                </div>
                <div className="p-6 overflow-y-auto">
                    {pendingTrainingList.length === 0 ? (
                        <EmptyState icon={<CheckCircle size={40} />} title="Training up to date" subtitle="All employees have completed their assigned training." positive />
                    ) : (
                        <div className="space-y-3">
                            {pendingTrainingList.map((item, idx) => (
                                <div key={idx} className="p-4 border border-slate-200 rounded-lg hover:bg-slate-50 transition-colors flex justify-between items-center">
                                    <div className="flex items-center gap-4">
                                        <div className="w-10 h-10 rounded-lg bg-amber-100 text-amber-600 flex items-center justify-center font-bold">
                                            {item.toolName.substring(0, 2)}
                                        </div>
                                        <div>
                                            <div className="font-bold text-slate-800">{item.toolName}</div>
                                            <div className="text-sm text-slate-500">{item.count} employee{item.count !== 1 ? 's' : ''} pending</div>
                                        </div>
                                    </div>
                                    <button
                                      onClick={() => { setShowPendingTrainingModal(false); navigate('/admin/training'); }}
                                      className="text-sm text-indigo-600 font-medium hover:underline flex items-center gap-1"
                                    >
                                        View Details <ArrowRight size={14} />
                                    </button>
                                </div>
                            ))}
                        </div>
                    )}
                </div>
                 <div className="p-4 border-t border-slate-100 bg-slate-50 flex justify-between items-center">
                    <button onClick={() => setShowPendingTrainingModal(false)} className="px-4 py-2 bg-white border border-slate-200 text-slate-700 font-medium rounded-lg hover:bg-slate-100">
                        Close
                    </button>
                    {pendingTrainingList.length > 0 && (
                      <button
                        onClick={() => { setShowPendingTrainingModal(false); navigate('/admin/training'); }}
                        className="px-4 py-2 bg-indigo-600 text-white font-medium rounded-lg hover:bg-indigo-700 flex items-center gap-2"
                      >
                        Go to Training <ArrowRight size={16} />
                      </button>
                    )}
                </div>
            </div>
        </div>
      )}

      {/* Pending accesses Modal */}
      {showAccessToProvideModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 backdrop-blur-sm p-4 animate-in fade-in duration-200">
            <div className="bg-white rounded-xl shadow-xl w-full max-w-2xl max-h-[90vh] flex flex-col overflow-hidden">
                <div className="flex justify-between items-center p-6 border-b border-slate-100 bg-slate-50">
                    <h3 className="text-lg font-bold text-slate-800">Pending accesses</h3>
                    <button onClick={() => setShowAccessToProvideModal(false)} className="text-slate-400 hover:text-slate-600">
                        <X size={24} />
                    </button>
                </div>
                <div className="p-6 overflow-y-auto">
                    {accessToProvideCount === 0 ? (
                        <EmptyState icon={<CheckCircle size={40} />} title="No access tasks" subtitle="No pending access grants. New requests will appear here when employees submit them." positive />
                    ) : (
                        <div className="space-y-4">
                            {accessTasksForAdmin.length > 0 && (
                                <div>
                                    <h4 className="text-xs font-bold text-slate-400 uppercase tracking-wider mb-2">For admin</h4>
                                    <div className="space-y-2">
                                        {accessTasksForAdmin.map(n => (
                                            <div key={n.id} className="p-3 border border-slate-200 rounded-lg bg-white flex justify-between items-center">
                                                <div>
                                                    <span className="font-medium text-slate-800">{n.requesterName}</span>
                                                    <span className="text-slate-500 text-sm"> — {n.type === 'ACCESS_REQUEST' ? 'Access' : 'Training'} for {n.toolName}</span>
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            )}
                            {accessTasksForResponsibles.length > 0 && (
                                <div>
                                    <h4 className="text-xs font-bold text-slate-400 uppercase tracking-wider mb-2">For responsibles (pending until done or revoked)</h4>
                                    <div className="space-y-2">
                                        {accessTasksForResponsibles.map(n => (
                                            <div key={n.id} className="p-3 border border-indigo-100 rounded-lg bg-indigo-50/50 flex justify-between items-center">
                                                <div>
                                                    <span className="font-medium text-slate-800">{n.toolName}</span>
                                                    <p className="text-xs text-slate-500 mt-0.5">{n.accessReason || n.trainingNeeds || 'Task for tool owner'}</p>
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            )}
                        </div>
                    )}
                </div>
                <div className="p-4 border-t border-slate-100 bg-slate-50 flex justify-between items-center">
                    <button onClick={() => setShowAccessToProvideModal(false)} className="px-4 py-2 bg-white border border-slate-200 text-slate-700 font-medium rounded-lg hover:bg-slate-100">
                        Close
                    </button>
                    {accessToProvideCount > 0 && onNavigateToNotifications && (
                        <button
                            onClick={() => { setShowAccessToProvideModal(false); onNavigateToNotifications(); }}
                            className="px-4 py-2 bg-indigo-600 text-white font-medium rounded-lg hover:bg-indigo-700 flex items-center gap-2"
                        >
                            Handle in Notifications <ArrowRight size={16} />
                        </button>
                    )}
                </div>
            </div>
        </div>
      )}

      {/* Cost per Employee Modal */}
      {showTotalSpendModal && (() => {
        const rankedTools = tools
          .map((tool) => ({
            tool,
            cost: getConvertedMonthlyCost(tool) * costMultiplier,
          }))
          .filter((row) => row.cost > 0)
          .sort((a, b) => b.cost - a.cost);
        const maxCost = rankedTools.length > 0 ? rankedTools[0].cost : 0;
        const periodSuffix = costPeriod === 'annual' ? '/yr' : '/mo';

        return (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 backdrop-blur-sm p-4 animate-in fade-in duration-200">
            <div className="bg-white rounded-xl shadow-xl w-full max-w-2xl max-h-[80vh] flex flex-col">
              <div className="p-5 border-b border-slate-100 flex justify-between items-center shrink-0">
                <div>
                  <h3 className="text-lg font-bold text-slate-800">Total Spend Breakdown</h3>
                  <p className="text-xs text-slate-500 mt-0.5">Tools sorted from highest to lowest cost ({costPeriod}).</p>
                </div>
                <button onClick={() => setShowTotalSpendModal(false)} className="p-1 text-slate-400 hover:text-slate-600"><X size={20} /></button>
              </div>

              <div className="p-5 overflow-y-auto space-y-3">
                {rankedTools.length > 0 ? rankedTools.map(({ tool, cost }) => {
                  const widthPct = maxCost > 0 ? (cost / maxCost) * 100 : 0;
                  return (
                    <div key={tool.id} className="border border-slate-100 rounded-lg p-3">
                      <div className="flex items-center justify-between mb-2">
                        <span className="text-sm font-medium text-slate-800">{tool.name}</span>
                        <span className="text-sm font-semibold text-slate-700">
                          {settings.currencySymbol}{cost.toLocaleString(undefined, { maximumFractionDigits: 0 })}{periodSuffix}
                        </span>
                      </div>
                      <div className="h-2 rounded-full bg-slate-100 overflow-hidden">
                        <div
                          className="h-full rounded-full bg-indigo-500"
                          style={{ width: `${Math.max(4, widthPct)}%` }}
                        />
                      </div>
                    </div>
                  );
                }) : (
                  <EmptyState icon={<DollarSign size={40} />} title="No cost data yet" subtitle="Add tools with pricing to see the spend ranking." />
                )}
              </div>

              <div className="p-4 border-t border-slate-100 bg-slate-50 shrink-0 flex justify-between items-center">
                <span className="text-sm text-slate-500">
                  Total: <span className="font-semibold text-slate-700">{settings.currencySymbol}{(totalMonthlyCost * costMultiplier).toLocaleString(undefined, { maximumFractionDigits: 0 })}{periodSuffix}</span>
                </span>
                <button onClick={() => setShowTotalSpendModal(false)} className="px-4 py-2 bg-white border border-slate-200 text-slate-700 font-medium rounded-lg hover:bg-slate-100">
                  Close
                </button>
              </div>
            </div>
          </div>
        );
      })()}

      {/* Cost per Employee Modal */}
      {showCostPerEmployeeModal && (() => {
        const employeeCosts = employees.map(emp => {
          const assignedTools = tools.filter(t => t.assignedUserIds?.includes(emp.id));
          const totalCost = assignedTools.reduce((acc, t) => {
            const assigneeCount = t.assignedUserIds?.length || 1;
            return acc + getConvertedMonthlyCost(t) / assigneeCount;
          }, 0);
          return { emp, assignedTools, totalCost };
        }).sort((a, b) => b.totalCost - a.totalCost);

        return (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 backdrop-blur-sm p-4 animate-in fade-in duration-200">
            <div className="bg-white rounded-xl shadow-xl w-full max-w-2xl max-h-[80vh] flex flex-col">
              <div className="p-5 border-b border-slate-100 flex justify-between items-center shrink-0">
                <h3 className="text-lg font-bold text-slate-800">Cost per Employee</h3>
                <button onClick={() => setShowCostPerEmployeeModal(false)} className="p-1 text-slate-400 hover:text-slate-600"><X size={20} /></button>
              </div>
              <div className="p-5 overflow-y-auto space-y-3">
                {employeeCosts.length > 0 ? employeeCosts.map(({ emp, assignedTools, totalCost }) => (
                  <details key={emp.id} className="group border border-slate-100 rounded-lg">
                    <summary className="flex items-center justify-between px-4 py-3 cursor-pointer hover:bg-slate-50 rounded-lg">
                      <div className="flex items-center gap-3">
                        <div className="w-8 h-8 rounded-full bg-indigo-100 text-indigo-600 flex items-center justify-center text-xs font-bold">
                          {emp.name.split(' ').map(n => n[0]).join('').slice(0, 2)}
                        </div>
                        <div>
                          <span className="font-medium text-slate-800 text-sm">{emp.name}</span>
                          <span className="text-xs text-slate-400 ml-2">{assignedTools.length} tool{assignedTools.length !== 1 ? 's' : ''}</span>
                        </div>
                      </div>
                      <span className="font-semibold text-slate-700 text-sm">{settings.currencySymbol}{totalCost.toLocaleString(undefined, { maximumFractionDigits: 0 })}/mo</span>
                    </summary>
                    {assignedTools.length > 0 && (
                      <div className="px-4 pb-3 space-y-1">
                        {assignedTools.map(t => {
                          const share = getConvertedMonthlyCost(t) / (t.assignedUserIds?.length || 1);
                          return (
                            <div key={t.id} className="flex justify-between items-center px-3 py-2 bg-slate-50 rounded-lg text-sm">
                              <span className="text-slate-600">{t.name}</span>
                              <span className="text-slate-500 font-medium">{settings.currencySymbol}{share.toLocaleString(undefined, { maximumFractionDigits: 0 })}/mo</span>
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </details>
                )) : (
                  <EmptyState icon={<User size={40} />} title="No employees yet" subtitle="Add employees to see cost breakdown." />
                )}
              </div>
              <div className="p-4 border-t border-slate-100 bg-slate-50 shrink-0 flex justify-between items-center">
                <span className="text-sm text-slate-500">
                  Average: <span className="font-semibold text-slate-700">{settings.currencySymbol}{avgCostPerEmployee.toLocaleString(undefined, { maximumFractionDigits: 0 })}/mo</span>
                </span>
                <button onClick={() => setShowCostPerEmployeeModal(false)} className="px-4 py-2 bg-white border border-slate-200 text-slate-700 font-medium rounded-lg hover:bg-slate-100">
                  Close
                </button>
              </div>
            </div>
          </div>
        );
      })()}

      {/* Budget Owners Modal */}
      {showBudgetOwnersModal && (() => {
        const unownedTools = tools.filter(t => !t.powerUserId);
        const ownerDetails = costByOwner.map(owner => {
          const emp = employees.find(e => e.name === owner.name);
          const ownedTools = emp ? tools.filter(t => t.powerUserId === emp.id) : [];
          return { ...owner, tools: ownedTools };
        });

        return (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 backdrop-blur-sm p-4 animate-in fade-in duration-200">
            <div className="bg-white rounded-xl shadow-xl w-full max-w-2xl max-h-[80vh] flex flex-col">
              <div className="p-5 border-b border-slate-100 flex justify-between items-center shrink-0">
                <h3 className="text-lg font-bold text-slate-800">Budget Ownership Breakdown</h3>
                <button onClick={() => setShowBudgetOwnersModal(false)} className="p-1 text-slate-400 hover:text-slate-600"><X size={20} /></button>
              </div>
              <div className="p-5 overflow-y-auto space-y-5">
                {ownerDetails.map(owner => (
                  <div key={owner.name}>
                    <div className="flex items-center justify-between mb-2">
                      <h4 className="font-semibold text-slate-800">{owner.name}</h4>
                      <span className="text-sm font-medium text-slate-600">
                        {owner.tools.length} tool{owner.tools.length !== 1 ? 's' : ''} · {settings.currencySymbol}{owner.cost.toLocaleString()}/mo
                      </span>
                    </div>
                    <div className="space-y-1">
                      {owner.tools.map(t => (
                        <div key={t.id} className="flex justify-between items-center px-3 py-2 bg-slate-50 rounded-lg text-sm">
                          <span className="text-slate-700">{t.name}</span>
                          <span className="text-slate-500 font-medium">{settings.currencySymbol}{getConvertedMonthlyCost(t).toLocaleString()}/mo</span>
                        </div>
                      ))}
                    </div>
                  </div>
                ))}

                {unownedTools.length > 0 && (
                  <div>
                    <div className="flex items-center justify-between mb-2">
                      <h4 className="font-semibold text-amber-700 flex items-center gap-2">
                        <AlertCircle size={16} /> No owner assigned
                      </h4>
                      <span className="text-sm font-medium text-amber-600">
                        {unownedTools.length} tool{unownedTools.length !== 1 ? 's' : ''} · {settings.currencySymbol}{unownedTools.reduce((acc, t) => acc + getConvertedMonthlyCost(t), 0).toLocaleString()}/mo
                      </span>
                    </div>
                    <div className="space-y-1">
                      {unownedTools.map(t => (
                        <div key={t.id} className="flex justify-between items-center px-3 py-2 bg-amber-50 rounded-lg text-sm">
                          <span className="text-amber-800">{t.name}</span>
                          <span className="text-amber-600 font-medium">{settings.currencySymbol}{getConvertedMonthlyCost(t).toLocaleString()}/mo</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {ownerDetails.length === 0 && unownedTools.length === 0 && (
                  <EmptyState icon={<User size={40} />} title="No tools yet" subtitle="Add tools and assign owners to see budget breakdown." />
                )}
              </div>
              <div className="p-4 border-t border-slate-100 bg-slate-50 shrink-0">
                <button onClick={() => setShowBudgetOwnersModal(false)} className="px-4 py-2 bg-white border border-slate-200 text-slate-700 font-medium rounded-lg hover:bg-slate-100">
                  Close
                </button>
              </div>
            </div>
          </div>
        );
      })()}

      {/* Knowledge scores modal */}
      {showKnowledgeScoreModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 backdrop-blur-sm p-4 animate-in fade-in duration-200">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-2xl max-h-[80vh] flex flex-col">
            <div className="p-5 border-b border-slate-100 flex justify-between items-center shrink-0">
              <h3 className="text-lg font-bold text-slate-800">
                {knowledgeScoreType === 'proficiency' ? 'Tools by Proficiency' : 'Tools by Satisfaction'}
              </h3>
              <button onClick={() => setShowKnowledgeScoreModal(false)} className="p-1 text-slate-400 hover:text-slate-600"><X size={20} /></button>
            </div>
            <div className="p-5 overflow-y-auto space-y-2">
              {knowledgeScoreRows.length > 0 ? knowledgeScoreRows.map((row) => (
                <div key={row.toolName} className="flex items-center justify-between px-3 py-2.5 bg-slate-50 rounded-lg">
                  <span className="text-sm font-medium text-slate-700">{row.toolName}</span>
                  <span className={`text-sm font-semibold ${row.hasScore ? 'text-slate-800' : 'text-slate-400 italic'}`}>
                    {row.hasScore ? `${row.score.toFixed(1)}/10` : 'Empty'}
                  </span>
                </div>
              )) : (
                <EmptyState icon={<Activity size={40} />} title="No tools yet" subtitle="Add tools to see score details." />
              )}
            </div>
            <div className="p-4 border-t border-slate-100 bg-slate-50 shrink-0 flex justify-end">
              <button onClick={() => setShowKnowledgeScoreModal(false)} className="px-4 py-2 bg-white border border-slate-200 text-slate-700 font-medium rounded-lg hover:bg-slate-100">
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// =============================================
// Criticality Breakdown widget
// =============================================
const COLOR_MAP_DASH: Record<string, { bg: string; text: string; border: string }> = {
  red: { bg: 'bg-red-100', text: 'text-red-700', border: 'border-red-200' },
  orange: { bg: 'bg-orange-100', text: 'text-orange-700', border: 'border-orange-200' },
  amber: { bg: 'bg-amber-100', text: 'text-amber-700', border: 'border-amber-200' },
  emerald: { bg: 'bg-emerald-100', text: 'text-emerald-700', border: 'border-emerald-200' },
  blue: { bg: 'bg-blue-100', text: 'text-blue-700', border: 'border-blue-200' },
  purple: { bg: 'bg-purple-100', text: 'text-purple-700', border: 'border-purple-200' },
};

function CriticalityBreakdown({ tools, governanceTasks, settings }: { tools: Tool[]; governanceTasks: GovernanceTask[]; settings: AppSettings }) {
  const nav = useNavigate();
  const levels = [...(settings.criticalityLevels ?? DEFAULT_CRITICALITY_LEVELS)].sort((a, b) => a.sortOrder - b.sortOrder);
  const todayStr = new Date().toISOString().slice(0, 10);

  const toolIdsByLevel = new Map<string, Set<string>>();
  levels.forEach(level => {
    const ids = new Set(tools.filter(t => t.importance === level.id).map(t => t.id));
    toolIdsByLevel.set(level.id, ids);
  });

  const data = levels.map(level => {
    const toolIds = toolIdsByLevel.get(level.id) ?? new Set<string>();
    const toolCount = toolIds.size;
    const overdueCount = (governanceTasks || []).filter(t =>
      !t.isCompleted && t.dueDate && t.dueDate < todayStr && t.toolId && toolIds.has(t.toolId)
    ).length;
    return { level, toolCount, overdueCount };
  });

  const totalTools = tools.length || 1;
  const compactOverdueBadge = levels.length >= 4;

  return (
    <div data-testid="criticality-breakdown" className="bg-white rounded-xl shadow-sm border border-slate-200 p-5">
      <div className="flex items-center justify-between mb-4">
        <h3 className="font-bold text-slate-700 flex items-center gap-2">
          <Database size={18} className="text-indigo-600" /> Tools by Criticality
        </h3>
        <span className="text-sm font-semibold text-slate-600 bg-slate-100 px-3 py-1 rounded-full">
          {tools.length} tool{tools.length !== 1 ? 's' : ''} total
        </span>
      </div>
      <div className={`grid grid-cols-1 gap-4 mb-4 ${levels.length <= 3 ? 'md:grid-cols-3' : levels.length === 4 ? 'md:grid-cols-4' : 'md:grid-cols-5'}`}>
        {data.map(({ level, toolCount, overdueCount }) => {
          const cls = COLOR_MAP_DASH[level.color] ?? COLOR_MAP_DASH.blue;
          return (
            <div key={level.id} className={`rounded-lg border ${cls.border} ${cls.bg} p-4 cursor-pointer hover:shadow-md transition-shadow`} onClick={() => nav(`/admin/inventory?importance=${encodeURIComponent(level.id)}`)}>
              <div className="flex justify-between items-start">
                <div>
                  <span className={`text-sm font-semibold ${cls.text}`}>{level.label}</span>
                  <div className="text-2xl font-bold text-slate-800 mt-1">{toolCount}</div>
                  <div className="text-xs text-slate-500">{((toolCount / totalTools) * 100).toFixed(0)}% of stack</div>
                </div>
                {overdueCount > 0 && (
                  <span className="flex items-center gap-1 text-xs font-medium bg-red-200 text-red-800 px-2 py-0.5 rounded-full whitespace-nowrap">
                    <AlertCircle size={10} />
                    {overdueCount}
                    {!compactOverdueBadge && ' overdue'}
                  </span>
                )}
              </div>
              <div className="mt-2 h-2 rounded-full bg-white/60 overflow-hidden">
                <div className={`h-full rounded-full ${cls.text.replace('text-', 'bg-').replace('700', '400')}`} style={{ width: `${(toolCount / totalTools) * 100}%` }} />
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}